import { createContext, useContext, useEffect, useRef } from 'react';

export type DrawerSaveApi = { save: () => void; saving: boolean };

/**
 * Set by the NX-4682 dashboard quick-edit drawer so an embedded employee-detail
 * tab can surface its Save in the always-visible drawer header. `null` on the
 * standalone employee-detail page (where each tab keeps its own in-form Save).
 */
export const DrawerSaveContext = createContext<
  ((api: DrawerSaveApi | null) => void) | null
>(null);

/**
 * Register the active tab's Save with the quick-edit drawer header (NX-4682).
 *
 * Returns `true` when rendered inside the drawer AND editable, so the tab can
 * hide its own in-form Save button. Returns `false` (no-op) on the standalone
 * detail page.
 *
 * @param save    Persists the tab; invoked by the header Save button.
 * @param saving  Whether a save is in flight (drives the header button spinner).
 * @param enabled Pass `false` for read-only tabs so no header Save is registered.
 */
export function useDrawerSave(
  save: () => void,
  saving: boolean,
  enabled = true,
): boolean {
  const register = useContext(DrawerSaveContext);
  const active = !!register && enabled;
  // Keep the latest save in a ref so the registered callback never goes stale
  // and the effect doesn't re-run (and loop) on every render.
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    if (!active || !register) return;
    register({ save: () => saveRef.current(), saving });
    return () => register(null);
  }, [active, register, saving]);
  return active;
}
